import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { formatDateOnly, getFormatConfig, initializeFormats } from '../../../utils/global';
import {
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateInputLabelProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const LINE_STATUSES = ['ACTIVE', 'INACTIVE'];
const EXPIRY_WARNING_DAYS = 30;

const defaultRateForm = {
  position_pay_rate_id: '',
  effective_from_date: '',
  effective_to_date: '',
  basic_hourly_rate: '',
  weekly_standard_hours: '40',
  status: 'ACTIVE',
};

const mapRateRow = (item) => ({
  id: item.position_pay_rate_id || item.positionPayRateId,
  position_pay_rate_id: item.position_pay_rate_id || item.positionPayRateId,
  effective_from_date: (item.effective_from_date || item.effectiveFromDate || '').slice(0, 10),
  effective_to_date: (item.effective_to_date || item.effectiveToDate || '').slice(0, 10),
  basic_hourly_rate: item.basic_hourly_rate ?? item.basicHourlyRate ?? 0,
  weekly_standard_hours: item.weekly_standard_hours ?? item.weeklyStandardHours ?? 40,
  status: item.status || 'ACTIVE',
});

const parseDateOnly = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const isEffectiveOn = (row, onDate) => {
  const from = parseDateOnly(row.effective_from_date);
  const to = parseDateOnly(row.effective_to_date);
  if (from && onDate < from) return false;
  if (to && onDate > to) return false;
  return true;
};

const rangesOverlap = (aFrom, aTo, bFrom, bTo) => {
  if (!aFrom || !bFrom) return false;
  const aEnd = aTo || new Date(9999, 11, 31);
  const bEnd = bTo || new Date(9999, 11, 31);
  return aFrom <= bEnd && bFrom <= aEnd;
};

const addOneDay = (date) => {
  if (!date) return null;
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
  return next;
};

const formatIsoDate = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const extractApiError = (err, fallback) =>
  err?.error || err?.message || err?.detail || fallback;

const PositionPayRatesSection = forwardRef(({
  positionRow,
  token,
  baseUrl,
  tenantId,
  propertyId,
  showError,
  showWarning,
  showSuccess,
  active = true,
  hideToolbar = false,
  onSelectionChange,
}, ref) => {
  const { canAdd, canEdit } = useFunctionPermissions(FC.WORKFORCE_OPTIMIZATION);
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState([]);
  const [selection, setSelection] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [rateForm, setRateForm] = useState(defaultRateForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [formatConfig, setFormatConfig] = useState(getFormatConfig());

  const notifyError = (message) => {
    setFormError(message);
    if (showWarning) {
      showWarning(message);
    } else {
      showError(message);
    }
  };

  const rolePositionId = positionRow?.role_position_id || positionRow?.id;
  const rolePositionName = positionRow?.role_name || 'Position';

  const authHeaders = useMemo(
    () => ({
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    }),
    [token]
  );

  const loadRates = useCallback(async () => {
    if (!rolePositionId || !tenantId || !propertyId) {
      setRates([]);
      return;
    }
    setLoading(true);
    try {
      const query = new URLSearchParams({
        tenant_id: tenantId,
        property_id: propertyId,
        role_position_id: rolePositionId,
      });
      const res = await fetch(`${baseUrl}conductor/workforce-position-pay-rate?${query}`, {
        headers: authHeaders,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to load pay rates');
      }
      const data = await res.json();
      setRates((Array.isArray(data) ? data : []).map(mapRateRow));
    } catch (error) {
      showError(error.message || 'Failed to load pay rates');
      setRates([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, baseUrl, propertyId, rolePositionId, showError, tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    initializeFormats(tenantId).then(() => setFormatConfig(getFormatConfig()));
  }, [tenantId]);

  useEffect(() => {
    if (!active) return;
    setSelection([]);
    loadRates();
  }, [active, loadRates]);

  const selectedRow = useMemo(
    () => rates.find((row) => row.id === selection[0]) || null,
    [rates, selection]
  );

  const expiryWarning = useMemo(() => {
    if (!rates.length) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const covering = rates
      .filter((row) => isEffectiveOn(row, today))
      .sort((a, b) => {
        const aFrom = parseDateOnly(a.effective_from_date)?.getTime() || 0;
        const bFrom = parseDateOnly(b.effective_from_date)?.getTime() || 0;
        return bFrom - aFrom;
      })[0];

    if (!covering) {
      return 'No pay rate is currently in effect for this position. Add a rate that covers today, or extend an existing period.';
    }

    if (!covering.effective_to_date) {
      return null;
    }

    const toDate = parseDateOnly(covering.effective_to_date);
    if (!toDate) return null;

    const msPerDay = 24 * 60 * 60 * 1000;
    const daysLeft = Math.round((toDate.getTime() - today.getTime()) / msPerDay);
    if (daysLeft < 0 || daysLeft > EXPIRY_WARNING_DAYS) {
      return null;
    }

    const hasSuccessor = rates.some((row) => {
      if (row.id === covering.id) return false;
      const from = parseDateOnly(row.effective_from_date);
      return from && from > toDate;
    });

    if (hasSuccessor) {
      return null;
    }

    const endLabel = formatDateOnly(covering.effective_to_date, formatConfig.DateFormat);
    if (daysLeft === 0) {
      return `Current pay rate ends today (${endLabel}). No next pay rate is scheduled after it expires.`;
    }
    return `Current pay rate ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (${endLabel}). No next pay rate is scheduled after it expires.`;
  }, [rates, formatConfig.DateFormat]);

  useEffect(() => {
    if (onSelectionChange) onSelectionChange(selectedRow);
  }, [onSelectionChange, selectedRow]);

  const openAdd = () => {
    if (!canAdd) return;
    setFormError('');
    setRateForm(defaultRateForm);
    setFormOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setFormError('');
    setRateForm({
      position_pay_rate_id: selectedRow.position_pay_rate_id,
      effective_from_date: selectedRow.effective_from_date,
      effective_to_date: selectedRow.effective_to_date,
      basic_hourly_rate: selectedRow.basic_hourly_rate,
      weekly_standard_hours: selectedRow.weekly_standard_hours,
      status: selectedRow.status,
    });
    setFormOpen(true);
  };

  const updateRateForm = (patch) => {
    setFormError('');
    setRateForm((prev) => ({ ...prev, ...patch }));
  };

  const findOverlapMessage = () => {
    const from = parseDateOnly(rateForm.effective_from_date);
    const to = parseDateOnly(rateForm.effective_to_date);
    if (!from) return '';

    const conflicting = rates.find((row) => {
      if (
        rateForm.position_pay_rate_id &&
        String(row.position_pay_rate_id) === String(rateForm.position_pay_rate_id)
      ) {
        return false;
      }
      return rangesOverlap(
        from,
        to,
        parseDateOnly(row.effective_from_date),
        parseDateOnly(row.effective_to_date)
      );
    });

    if (!conflicting) return '';

    const otherFrom = formatDateOnly(conflicting.effective_from_date, formatConfig.DateFormat);
    const otherTo = conflicting.effective_to_date
      ? formatDateOnly(conflicting.effective_to_date, formatConfig.DateFormat)
      : 'open-ended';
    const nextStart = conflicting.effective_to_date
      ? formatDateOnly(
          formatIsoDate(addOneDay(parseDateOnly(conflicting.effective_to_date))),
          formatConfig.DateFormat
        )
      : null;

    let message = `Date range overlaps an existing rate (${otherFrom} – ${otherTo}). Ranges cannot overlap.`;
    if (nextStart) {
      message += ` Start the new rate on or after ${nextStart}.`;
    }
    return message;
  };

  useImperativeHandle(ref, () => ({
    openAdd,
    openEdit: () => openEdit(),
  }), [selectedRow, canAdd, canEdit]);

  const columns = useMemo(
    () => [
      {
        field: 'effective_from_date',
        headerName: 'From Date',
        width: 120,
        resizable: true,
        valueFormatter: (params) =>
          params.value ? formatDateOnly(params.value, formatConfig.DateFormat) : '',
      },
      {
        field: 'effective_to_date',
        headerName: 'To Date',
        width: 120,
        resizable: true,
        valueFormatter: (params) =>
          params.value ? formatDateOnly(params.value, formatConfig.DateFormat) : '',
      },
      { field: 'basic_hourly_rate', headerName: 'Hourly Rate', width: 110, resizable: true },
      { field: 'weekly_standard_hours', headerName: 'Weekly Hrs', width: 110, resizable: true },
      { field: 'status', headerName: 'Status', width: 100, resizable: true },
    ],
    [formatConfig.DateFormat]
  );

  const saveRate = async () => {
    if (!rateForm.effective_from_date) {
      notifyError('From Date is required.');
      return;
    }

    const from = parseDateOnly(rateForm.effective_from_date);
    const to = parseDateOnly(rateForm.effective_to_date);
    if (from && to && to < from) {
      notifyError('To Date cannot be before From Date.');
      return;
    }

    const overlapMessage = findOverlapMessage();
    if (overlapMessage) {
      notifyError(overlapMessage);
      return;
    }

    const isEdit = Boolean(rateForm.position_pay_rate_id);
    const url = isEdit
      ? `${baseUrl}conductor/workforce-position-pay-rate/${rateForm.position_pay_rate_id}`
      : `${baseUrl}conductor/workforce-position-pay-rate`;
    const body = {
      tenant_id: tenantId,
      property_id: propertyId,
      role_position_id: rolePositionId,
      role_position_name: rolePositionName,
      effective_from_date: rateForm.effective_from_date,
      effective_to_date: rateForm.effective_to_date || null,
      basic_hourly_rate: Number(rateForm.basic_hourly_rate) || 0,
      daily_rate: 0,
      weekly_standard_hours: Number(rateForm.weekly_standard_hours) || 40,
      status: rateForm.status,
    };

    setSaving(true);
    try {
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        notifyError(extractApiError(err, 'Failed to save pay rate.'));
        return;
      }
      setFormOpen(false);
      setFormError('');
      showSuccess(isEdit ? 'Pay rate updated.' : 'Pay rate added.');
      loadRates();
    } catch (error) {
      notifyError(error.message || 'Failed to save pay rate.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, ...(hideToolbar ? {} : { p: 1.5, gap: 1 }) }}>
      {!hideToolbar ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title="Add Pay Rate">
            <IconButton size="small" onClick={openAdd} disabled={!rolePositionId || !canAdd}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit Pay Rate">
            <span>
              <IconButton size="small" onClick={openEdit} disabled={!selectedRow || !canEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ) : null}

      {expiryWarning ? (
        <Alert
          severity="warning"
          sx={{
            py: 0.5,
            mb: hideToolbar ? 1 : 0,
            '& .MuiAlert-message': { fontSize: '0.8125rem' },
          }}
        >
          {expiryWarning}
        </Alert>
      ) : null}

      <Paper sx={{ flex: 1, borderRadius: 0, overflow: 'hidden', boxShadow: 'none', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <DataGridPro
          rows={rates}
          columns={columns}
          loading={loading}
          rowSelectionModel={selection}
          onRowSelectionModelChange={(model) => setSelection(model)}
          disableMultipleRowSelection
          pagination
          pageSizeOptions={[10, 25]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{ ...harmonateDataGridSx, border: 'none', flex: 1, minHeight: 0 }}
        />
      </Paper>

      <Dialog
        open={formOpen}
        onClose={() => {
          if (saving) return;
          setFormOpen(false);
          setFormError('');
        }}
        maxWidth="xs"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>{rateForm.position_pay_rate_id ? 'Edit Pay Rate' : 'Add Pay Rate'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          {formError ? (
            <Alert severity="warning" sx={{ '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
              {formError}
            </Alert>
          ) : null}
          <TextField
            label="From Date"
            type="date"
            value={rateForm.effective_from_date}
            onChange={(e) => updateRateForm({ effective_from_date: e.target.value })}
            fullWidth
            size="small"
            InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            required
            error={Boolean(formError)}
          />
          <TextField
            label="To Date"
            type="date"
            value={rateForm.effective_to_date}
            onChange={(e) => updateRateForm({ effective_to_date: e.target.value })}
            fullWidth
            size="small"
            InputLabelProps={{ ...harmonateInputLabelProps, shrink: true }}
            helperText="Required before adding a successor rate. Ranges cannot overlap."
            error={Boolean(formError)}
          />
          <TextField
            label="Hourly Rate"
            type="number"
            value={rateForm.basic_hourly_rate}
            onChange={(e) => updateRateForm({ basic_hourly_rate: e.target.value })}
            fullWidth
            size="small"
            InputLabelProps={harmonateInputLabelProps}
            inputProps={{ min: 0, step: 0.01 }}
          />
          <TextField
            label="Weekly Hrs"
            type="number"
            value={rateForm.weekly_standard_hours}
            onChange={(e) => updateRateForm({ weekly_standard_hours: e.target.value })}
            fullWidth
            size="small"
            InputLabelProps={harmonateInputLabelProps}
            inputProps={{ min: 0, step: 0.5 }}
          />
          <TextField
            select
            label="Status"
            value={rateForm.status}
            onChange={(e) => updateRateForm({ status: e.target.value })}
            fullWidth
            size="small"
            InputLabelProps={harmonateInputLabelProps}
            helperText="Auto-set from dates: only the rate covering today stays ACTIVE."
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            {LINE_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setFormOpen(false);
              setFormError('');
            }}
            disabled={saving}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={saveRate} disabled={saving} sx={harmonateProceedButtonSx}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default PositionPayRatesSection;
